import { styled } from "@mui/material/styles";
import { ChevronRightIcon, reducedMotion, Spinner, StatusDot, thinScrollbar, usePresence } from "@UI";
import { getToolName, isToolUIPart } from "ai";
import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatToolName, isClientReadTool, isMutatingTool, readToolRunLength, toolIcon, toolStatusTone, toolTarget } from "../utils/tool-utils";
export const StepRow = styled("button")({
    width: "100%",
    minHeight: "var(--j-row)",
    display: "flex",
    alignItems: "center",
    gap: "var(--j-sp-2)",
    padding: 0,
    border: 0,
    borderRadius: "var(--j-r-sm)",
    background: "transparent",
    fontSize: "var(--j-fs-sm)",
    lineHeight: "var(--j-lh-md)",
    color: "var(--j-ink-2)",
    textAlign: "left",
    "button&": {
        cursor: "pointer",
        "&:hover .step-label": {
            color: "var(--j-ink)"
        }
    },
    "& > .step-icon": {
        width: 14,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        color: "var(--j-ink-3)"
    },
    "& > .step-label": {
        flex: 1,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        transition: "color var(--j-dur-fade)"
    },
    "& .step-target": {
        color: "var(--j-ink)",
        fontWeight: 500
    },
    "&[data-error=true] > .step-label": {
        color: "var(--j-err)"
    }
});
export const StepChevron = styled(ChevronRightIcon)({
    flexShrink: 0,
    color: "var(--j-ink-3)",
    transition: "transform var(--j-dur-fade) var(--j-ease)",
    "[aria-expanded=true] > &": {
        transform: "rotate(90deg)"
    },
    [reducedMotion]: {
        transition: "none"
    }
});
export const reveal = {
    opacity: 0,
    transform: "translateY(-4px)",
    transition: "opacity var(--j-dur-fade) var(--j-ease), transform var(--j-dur-fade) var(--j-ease)",
    "&[data-visible=true]": {
        opacity: 1,
        transform: "none"
    },
    [reducedMotion]: {
        transition: "none"
    }
};
const Detail = styled("pre")({
    ...reveal,
    margin: "0 0 var(--j-sp-1) 22px",
    padding: "var(--j-sp-2)",
    maxHeight: 220,
    overflow: "auto",
    ...thinScrollbar,
    borderRadius: "var(--j-r-sm)",
    background: "var(--j-panel)",
    border: "1px solid var(--j-line-soft)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: "var(--j-fs-xs)",
    lineHeight: "var(--j-lh-xs)",
    color: "var(--j-ink-2)",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere"
});
const MARK = "\u0001";
function runDisplayOf(output) {
    const display = output?.display;
    return typeof display?.domain === "string" && typeof display.action === "string" ? display : null;
}
function runLabel(display, t, hasKey) {
    const key = `chat.run.${display.domain}.${display.action}`;
    if (!hasKey(key)) return null;
    return [
        t(key),
        display.rows !== undefined && t("chat.run.results", {
            count: display.rows
        })
    ].filter(Boolean).join(" · ");
}
export const labelName = (part)=>{
    const toolName = getToolName(part);
    return toolName === "exec" && part.input?.write === true ? "exec_write" : toolName;
};
export function toolStepLabel(part, t, hasKey, isLive) {
    const toolName = getToolName(part);
    const tone = toolStatusTone(part, isLive);
    const name = formatToolName(labelName(part), t);
    if (tone === "error") return t("chat.activity.failed", {
        label: name
    });
    const display = part.state === "output-available" ? runDisplayOf(part.output) : null;
    const shellLabel = display && runLabel(display, t, hasKey);
    if (shellLabel) return shellLabel;
    const key = `chat.tool.${toolName}.${tone === "done" ? "done" : "running"}`;
    if (!hasKey(key)) return tone === "running" ? t("chat.activity.running", {
        label: name
    }) : name;
    const output = part.state === "output-available" ? part.output : undefined;
    const target = toolTarget(toolName, part.input, output) ?? t(`chat.tool.${toolName}.target`);
    const label = t(key, {
        target: MARK
    });
    const [before, after = ""] = (tone === "stopped" ? label.replace(/…$/, "") : label).split(MARK);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, before, /*#__PURE__*/ React.createElement("span", {
        className: "step-target"
    }, target), after);
}
const TONE_ICON = {
    running: /*#__PURE__*/ React.createElement(Spinner, {
        size: "sm"
    }),
    stopped: /*#__PURE__*/ React.createElement(StatusDot, {
        status: "idle"
    }),
    error: /*#__PURE__*/ React.createElement(StatusDot, {
        status: "err"
    })
};
export function ToolPart({ part, partIndex, ctx }) {
    const { t, i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const detail = usePresence(isOpen);
    const detailId = useId();
    if (!isToolUIPart(part)) return null;
    const count = readToolRunLength(ctx.message.parts, partIndex);
    if (count === 0) return null;
    const toolName = getToolName(part);
    const tone = toolStatusTone(part, ctx.isStreaming);
    const Icon = toolIcon(labelName(part));
    const output = part.state === "output-available" ? part.output : undefined;
    const details = runDisplayOf(output) ? output.text : output ?? part.input;
    const hasDetails = !isMutatingTool(toolName) && !isClientReadTool(toolName) && details !== undefined && details !== null;
    const content = /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("span", {
        className: "step-icon",
        "data-live": tone === "running" || tone === "error"
    }, tone === "done" ? /*#__PURE__*/ React.createElement(Icon, {
        size: 14
    }) : TONE_ICON[tone]), /*#__PURE__*/ React.createElement("span", {
        className: "step-label"
    }, count > 1 ? `${formatToolName(labelName(part), t)} ×${count}` : toolStepLabel(part, t, (key)=>i18n.exists(key), ctx.isStreaming)), hasDetails && /*#__PURE__*/ React.createElement(StepChevron, {
        size: 12
    }));
    if (!hasDetails) return /*#__PURE__*/ React.createElement(StepRow, {
        as: "div",
        "data-error": tone === "error"
    }, content);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(StepRow, {
        type: "button",
        "data-error": tone === "error",
        "aria-expanded": isOpen,
        "aria-controls": isOpen ? detailId : undefined,
        onClick: ()=>setIsOpen((open)=>!open)
    }, content), isOpen && detail.isMounted && /*#__PURE__*/ React.createElement(Detail, {
        id: detailId,
        "data-visible": detail.isVisible && isOpen
    }, typeof details === "string" ? details : JSON.stringify(details, null, 2)));
}
